import Vue from 'vue';

export default new Vue({
  name: 'ProfileSectionNavigationGuardian',
  data() {
    return {
      routeDisplayRules: {},
      hasInitialized: false,
    };
  },
  computed: {
    /**
     * @returns {Array} an array of availableRoutes to be displayed inside ProfileSection
     */
    availableRoutes() {
      return Object.entries(this.routeDisplayRules)
        .filter(([path, canShow]) => canShow)
        .map(([path]) => path);
    },
    /**
     * Return the first route from availableRoutes
     * @returns {(String|null)} path or null if nothing is allowed to show
     */
    defaultTab() {
      return this.availableRoutes[0] || null;
    },
  },
  methods: {
    /**
     * An essential function that need to be called to initialise this module.
     * This methods will initialize the `routeDisplayRules` with the provided
     * context and routeDefinisions.
     *
     * @param {Object} context
     * @param {User} context.user - contains the user object
     * @param {Object} context.permissions - contains profile section permission definition
     * @param {Array} routeDefinitions - an Array of route definitions containing { path and enable }
     */
    initialize(context, routeDefinitions) {
      routeDefinitions
        .forEach(({ path, meta }) => {
          const enable = meta.enable;
          if (enable == null) return;

          const shouldDisplay = typeof enable === 'function'
            ? enable(context)
            : !!enable;

          this.$set(
            this.routeDisplayRules,
            path,
            shouldDisplay,
          );
        });
      this.hasInitialized = true;
    },

    /**
     * A helper function that compute the current profileSection base path
     * @param {route} route - the route object from router's `to`, `from` or `this.$route`
     * @return {String} basePath - the basePath with both leading and trailing slash
     */
    getBasePath(route) {
      return (route.params.id == null) ? '/profile/' : `/u/${route.params.id}/`;
    },

    /**
     * Check if the current route is valid (i.e. accessible)
     * @param {String} path - name of the route
     * @return {Boolean} validity (accessibility) of the requested route
     */
    isRouteValid(path) {
      return !!this.routeDisplayRules[path];
    },

    /**
     *
     * @param {Route} to
     */
    performPreRouteActions(to) {
      ga('set', 'page', to.path);
      ga('send', 'pageview');
    },

    proceedToRoute(context, condition = true, fallbackDestination = undefined) {
      if (condition) {
        // Perform additional methods here on successful navigation
        this.performPreRouteActions(context.to);
        context.next(); // let route enter (redirection not required)
      } else {
        context.next(fallbackDestination);
      }
    },

    checkNavigation(to, from, next) {
      const navigationContext = { to, from, next };

      const requestedTab = to.meta.requestedTab;
      // Requested route is falsey when route is at base path or 404
      if (requestedTab != null) {
        if (!requestedTab) {
          return this.proceedToRoute(
            navigationContext,
            requestedTab === '',
            this.getBasePath(to), // redirect to base path
          );
        }
        const isRouteValid = this.isRouteValid(to.meta.requestedTab);

        // Conditionally route path base on route permission
        return this.proceedToRoute(
          navigationContext,
          isRouteValid,
          this.getBasePath(to), // redirect to base path
        );
      }

      this.proceedToRoute(navigationContext);
    },

    /**
     * Guard navigation for Profile Section
     * This function that can be used inside the router's before * hook /
     * navigation guard.
     */
    guardNavigation(to, from, next) {
      const retrier = setInterval(() => {
        if (this.hasInitialized) {
          this.checkNavigation(to, from, next);
          clearInterval(retrier);
        }
      }, 100);
    },
  },
});
